import { useEffect, useRef, useState } from 'react';
import { useSocket } from 'socket.io-react-hook';
import { _getToken } from 'src/Config/axiosApi';
import { UnwindLogEvent } from '../types/unwind-logs.types';

// 4 Hz is plenty for a "live tail" — humans cannot read faster than that
// and React's render cost scales linearly with state-update frequency.
// Old value (100ms) was 10 Hz, which kept the panel re-rendering even on
// a quiet rule because every interim WS event triggered a flush.
const FLUSH_INTERVAL_MS = 250;
// Bounded live ring. Smaller is better for render cost — every flush of
// new events spreads into a fresh array via React state, so a 250-row
// ring costs half as much per WS push as a 500-row ring. Older events
// stay reachable through the paginated history endpoint.
const MAX_RING = 250;

export interface UnwindLogsLiveState {
  isConnected: boolean;
  events: UnwindLogEvent[];
}

/**
 * Live tail for a single rule's dev-logs. Connects to /unwind-logs, subscribes
 * by ruleId, buffers incoming events and flushes to React state at most every
 * 100 ms (avoids re-render thrash under high event rate). Holds a bounded ring
 * of the most recent MAX_RING events; older ones are evicted as new ones arrive.
 *
 * Pair with `useUnwindLogs(ruleId)` for paginated history. The component is
 * responsible for de-duping if both feeds overlap.
 */
export function useUnwindLogsSocket(opts: {
  ruleId: string;
  enabled?: boolean;
}): UnwindLogsLiveState {
  const wsUrl = import.meta.env.VITE_POSITION_UNWINDER_URL;
  const accessToken = _getToken();
  const enabled = (opts.enabled ?? true) && !!opts.ruleId;

  const { socket } = useSocket((wsUrl || '') + '/unwind-logs', {
    enabled: enabled && !!wsUrl && !!accessToken,
    transports: ['websocket'],
    auth: (cb: (data: { token: string }) => void) =>
      cb({ token: _getToken() }),
  });

  const [state, setState] = useState<UnwindLogsLiveState>({
    isConnected: false,
    events: [],
  });

  const buf = useRef<UnwindLogEvent[]>([]);
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setState((s) => ({ ...s, isConnected: socket.connected }));
  }, [socket.connected]);

  // (Re)subscribe whenever the rule changes or we (re)connect.
  useEffect(() => {
    if (!socket.connected || !opts.ruleId) return;
    socket.emit('subscribe', { ruleId: opts.ruleId });
    return () => {
      socket.emit('unsubscribe');
    };
  }, [socket, socket.connected, opts.ruleId]);

  useEffect(() => {
    if (!enabled) return;

    const onEvent = (evt: UnwindLogEvent) => {
      // Defensive: ignore cross-rule leaks (server already filters).
      if (evt?.ruleId !== opts.ruleId) return;
      buf.current.push(evt);
      scheduleFlush();
    };
    const scheduleFlush = () => {
      if (flushTimer.current) return;
      flushTimer.current = setTimeout(() => {
        flushTimer.current = null;
        const incoming = buf.current;
        buf.current = [];
        if (incoming.length === 0) return;
        setState((s) => ({
          isConnected: socket.connected,
          // Newest first, ring-bounded.
          events: trimRing([...incoming.reverse(), ...s.events]),
        }));
      }, FLUSH_INTERVAL_MS);
    };

    socket.on('log', onEvent);
    return () => {
      socket.off('log', onEvent);
      if (flushTimer.current) {
        clearTimeout(flushTimer.current);
        flushTimer.current = null;
      }
    };
  }, [socket, enabled, opts.ruleId]);

  return state;
}

function trimRing<T>(arr: T[]): T[] {
  if (arr.length <= MAX_RING) return arr;
  return arr.slice(0, MAX_RING);
}
